
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Message, MessageContextType, MessageMetadata } from '@/types/insurance';
import { useAuth } from '@/context/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { logError } from '@/lib/errorLogger';

export interface UseChatMessagesResult {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  loading: boolean;
  loadMessages: () => Promise<void>;
  addMessage: (content: string, role: string, contextType?: MessageContextType, metadata?: MessageMetadata) => Promise<Message | null>;
  updateMessage: (messageId: string, updates: Partial<Message>) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  streamingMessageId: React.MutableRefObject<string | null>;
  isProcessingMessage: React.MutableRefObject<boolean>;
  processedMessageIds: React.MutableRefObject<Set<string>>;
  setIsStreaming: (isStreaming: boolean) => void;
  setCurrentStreamedContent: (content: string) => void;
}

export const useChatMessages = (chatId?: string, contextValue: any = null): UseChatMessagesResult => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [currentStreamedContent, setCurrentStreamedContent] = useState<string>('');
  const streamingMessageId = useRef<string | null>(null);
  const isProcessingMessage = useRef<boolean>(false);
  const processedMessageIds = useRef<Set<string>>(new Set());
  const { user } = useAuth();
  const { toast } = useToast();

  const loadMessages = useCallback(async () => {
    if (!chatId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });
        
      if (error) {
        throw error;
      }
      
      if (data) {
        const formattedMessages: Message[] = data.map(msg => ({
          id: msg.id,
          chat_id: msg.chat_id,
          role: msg.role as 'user' | 'assistant' | 'system',
          content: msg.content,
          created_at: msg.created_at,
          context_type: msg.context_type,
          metadata: msg.metadata as MessageMetadata | undefined,
          referenceId: msg.reference_id,
          isEmail: msg.is_email || false,
          templateId: msg.template_id
        }));
        
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      logError(error as Error, 'useChatMessages');
      toast({
        title: "Fehler",
        description: "Nachrichten konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [chatId, toast]);

  const addMessage = async (content: string, role: string, contextType?: MessageContextType, metadata?: MessageMetadata): Promise<Message | null> => {
    if (!chatId || !user) return null;
    
    const messageId = crypto.randomUUID();
    
    const newMessage: Message = {
      id: messageId,
      chat_id: chatId,
      role: role as 'user' | 'assistant' | 'system',
      content: content,
      context_type: contextType,
      metadata: metadata,
      isOptimistic: true
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    try {
      const { data: insertedMessage, error: insertError } = await supabase
        .from('messages')
        .insert([
          {
            id: messageId,
            chat_id: chatId,
            role: role,
            content: content,
            context_type: contextType,
            metadata: metadata ? JSON.stringify(metadata) : null
          }
        ])
        .select('*')
        .single();
        
      if (insertError) {
        throw insertError;
      }
      
      const finalMessage: Message = {
        id: insertedMessage.id,
        chat_id: insertedMessage.chat_id,
        role: insertedMessage.role as 'user' | 'assistant' | 'system',
        content: insertedMessage.content,
        created_at: insertedMessage.created_at,
        context_type: insertedMessage.context_type as MessageContextType | undefined,
        metadata: insertedMessage.metadata as MessageMetadata | undefined,
        referenceId: insertedMessage.reference_id,
        isEmail: insertedMessage.is_email || false,
        templateId: insertedMessage.template_id
      };
      
      setMessages(prev =>
        prev.map(msg => (msg.id === messageId ? finalMessage : msg))
      );
      
      return finalMessage;
    } catch (error) {
      console.error('Error adding message:', error);
      logError(error as Error, 'useChatMessages');
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gespeichert werden.",
        variant: "destructive"
      });
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      return null;
    }
  };

  const updateMessage = async (messageId: string, updates: Partial<Message>) => {
    try {
      // Optimistically update the local state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        )
      );
      
      // Prepare updates for Supabase, ensuring metadata is stringified
      const dbUpdates: any = { ...updates };
      if (updates.metadata) {
        dbUpdates.metadata = JSON.stringify(updates.metadata);
      }
      
      // Update the message in the database
      const { error } = await supabase
        .from('messages')
        .update(dbUpdates)
        .eq('id', messageId);
        
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating message:', error);
      logError(error as Error, 'useChatMessages');
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht aktualisiert werden.",
        variant: "destructive"
      });
      
      // Revert the local state on error
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.id === messageId) {
            // Revert to the original message state
            const originalMessage = messages.find(m => m.id === messageId);
            return originalMessage ? { ...originalMessage } : msg;
          }
          return msg;
        });
      });
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      // Optimistically remove the message from the local state
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      // Delete the message from the database
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
        
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      logError(error as Error, 'useChatMessages');
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gelöscht werden.",
        variant: "destructive"
      });
      
      // If there's an error, revert the local state
      loadMessages();
    }
  };

  useEffect(() => {
    if (chatId) {
      loadMessages();
    }
  }, [chatId, loadMessages]);

  return {
    messages,
    setMessages,
    loading,
    loadMessages,
    addMessage,
    updateMessage,
    deleteMessage,
    streamingMessageId,
    isProcessingMessage,
    processedMessageIds,
    setIsStreaming,
    setCurrentStreamedContent
  };
};
